import { Component, Inject, OnInit, Self } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { first } from 'rxjs/operators';

import CONSTANTS from '@constants';
import { environment } from '@environments/environment';

import browserHelper from '@utils/browser-helper';
import { AuthService } from '@app/shared/services/auth.service';
import { BackgroundBrandedService } from '@app/shared/modules/background-branded/services/background-branded.service';
import { CompanyResource } from '@app/shared/modules/resources/company.resource';
import { ToastsService } from '@app/shared/services/toasts.service';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false
})
export class LoginComponent implements OnInit {
  isLoaded = false;
  isInProgress = false;
  isSubmitted = false;
  animate = false;

  companyForm = new FormGroup({
    domainName: new FormControl('') // validated by domain-input
  });

  private submissionAttemptsRemainingToShake: number = 3;

  constructor(
    @Inject('Window') private window: Window,
    private auth: AuthService,
    private backgroundBrandedService: BackgroundBrandedService,
    private companyResource: CompanyResource,
    private toasts: ToastsService,
    private translateService: TranslateService
  ) {}

  get domainName() {
    return this.companyForm.get('domainName');
  }

  ngOnInit(): void {
    if (browserHelper.subdomain()) {
      this.auth.LOGIN_LINK.subscribe((loginLink: string) => {
        window.location.href = loginLink + window.location.search;
      });
      // leave deferred hanging, so the next state won't load until the redirect happens
    } else {
      this.backgroundBrandedService
        .fetchBranding()
        .pipe(first())
        .subscribe(
          () => {
            this.isLoaded = true;
            this.showCookiesError();
          },
          () => {
            this.isLoaded = true;
            this.showCookiesError();
          }
        );
    }
  }

  /**
   * Validates user input and redirect to company login page on success
   */
  validateCompany() {
    if (this.companyForm.invalid) {
      this.isSubmitted = true;
      return;
    }

    this.isInProgress = true;

    this.companyResource
      .getCompany(this.domainName.value)
      .pipe(first())
      .subscribe(
        () => this.redirectToCompanySubdomain(),
        (response: HttpErrorResponse) => {
          // Trigger shake animation to attract user attention
          if (this.submissionAttemptsRemainingToShake - 1 === 0) {
            this.animate = true;
          }

          this.toasts.showError({
            textTranslatable: {
              key: 'api-error.' + response.error.code,
              params: { supportLink: this.translateService.getLocalizedConstantString(CONSTANTS.WEB_LINKS.SUPPORT) }
            }
          });

          this.submissionAttemptsRemainingToShake--;
          this.isInProgress = false;
        }
      );
  }

  /**
   * Redirects to company subdomain login page
   */
  private redirectToCompanySubdomain(): void {
    this.auth.isRedesignActive$.pipe(first()).subscribe(isActive => {
      const baseUrl = `https://${this.domainName.value}.${environment.AUTH_BASE}`;
      const redirectUrl = isActive ? `${baseUrl}/site/login` : `${baseUrl}/login`;
      this.window.location.href = redirectUrl;
    });
  }

  private showCookiesError() {
    // Alert user that app should run with cookies
    if (!browserHelper.areCookiesAvailable) {
      setTimeout(() => {
        this.toasts.showError({ textTranslatable: 'common.cookies-are-disabled-error' });
      }, 100);
    }
  }
}
